import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  billingSessionService,
  CloseBillingSessionDto,
  CashDrawerCountDto,
  BillingSession,
  SessionSummary,
} from "../../services/billing-session.service";
import { useBillingSessionStore } from "../../stores/billingSessionStore";
import { showToast } from "../../lib/toast";

export function CloseBillingSessionPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { clearSession } = useBillingSessionStore();
  const [loading, setLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(true);
  const [session, setSession] = useState<BillingSession | null>(null);
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [step, setStep] = useState<"summary" | "cash-count">("summary");
  const [cashCount, setCashCount] = useState<CashDrawerCountDto>({
    notes2000: 0,
    notes500: 0,
    notes200: 0,
    notes100: 0,
    notes50: 0,
    notes20: 0,
    notes10: 0,
    coins: 0,
    notes: "",
  });

  useEffect(() => {
    if (id) {
      loadSessionData();
    }
  }, [id]);

  const loadSessionData = async () => {
    if (!id) return;
    try {
      setLoadingSession(true);
      const [sessionData, summaryData] = await Promise.all([
        billingSessionService.getById(id),
        billingSessionService.getSessionSummary(id),
      ]);
      setSession(sessionData);
      setSummary(summaryData);
    } catch (err: any) {
      showToast.error("Failed to load session data");
      navigate("/billing-sessions");
    } finally {
      setLoadingSession(false);
    }
  };

  const calculateTotal = () => {
    return (
      (cashCount.notes2000 || 0) * 2000 +
      (cashCount.notes500 || 0) * 500 +
      (cashCount.notes200 || 0) * 200 +
      (cashCount.notes100 || 0) * 100 +
      (cashCount.notes50 || 0) * 50 +
      (cashCount.notes20 || 0) * 20 +
      (cashCount.notes10 || 0) * 10 +
      (cashCount.coins || 0)
    );
  };

  const handleCashCountChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setCashCount((prev) => ({
      ...prev,
      [name]: name === "notes" ? value : parseInt(value) || 0,
    }));
  };

  const handleCloseSession = async () => {
    if (!id) return;

    try {
      setLoading(true);

      // First record the closing cash drawer count
      // The canonical close command persists the denomination evidence and
      // closes the session atomically.
      const closeData: CloseBillingSessionDto = {
        actualCash: calculateTotal(),
        notes: cashCount.notes,
        denominations: [
          { denominationPaise: 200_000, count: cashCount.notes2000 ?? 0 },
          { denominationPaise: 50_000, count: cashCount.notes500 ?? 0 },
          { denominationPaise: 20_000, count: cashCount.notes200 ?? 0 },
          { denominationPaise: 10_000, count: cashCount.notes100 ?? 0 },
          { denominationPaise: 5_000, count: cashCount.notes50 ?? 0 },
          { denominationPaise: 2_000, count: cashCount.notes20 ?? 0 },
          { denominationPaise: 1_000, count: cashCount.notes10 ?? 0 },
          { denominationPaise: 100, count: cashCount.coins ?? 0 },
        ],
        reasonCode: "OPERATOR_SESSION_CLOSE",
      };

      await billingSessionService.closeSession(id, closeData);
      // Clear global store so header syncs immediately
      clearSession();
      showToast.success("Billing session closed successfully");
      navigate("/billing-sessions");
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to close session");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loadingSession) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="text-center text-gray-500">Session not found</div>
      </div>
    );
  }

  const expectedCash =
    session.expectedCash ??
    (session.openingCash || 0) +
      (session.totalCashIn || 0) -
      (session.totalCashOut || 0);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Close Billing Session
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Session: {session.sessionNumber} • {session.shift} Shift
            {session.counter?.name ? ` • Counter: ${session.counter.name}` : ""}
          </p>
        </div>
        <button
          onClick={() => navigate("/billing-sessions")}
          className="text-gray-600 hover:text-gray-900"
        >
          ← Back
        </button>
      </div>

      {step === "summary" ? (
        <div className="space-y-6">
          {/* Session Summary Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Session Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="text-sm text-blue-600">Opening Cash</div>
                <div className="text-xl font-bold text-blue-800">
                  {formatCurrency(session.openingCash || 0)}
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="text-sm text-green-600">Total Sales</div>
                <div className="text-xl font-bold text-green-800">
                  {formatCurrency(summary?.totalSales || 0)}
                </div>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <div className="text-sm text-red-600">Returns</div>
                <div className="text-xl font-bold text-red-800">
                  {formatCurrency(summary?.totalReturns || 0)}
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="text-sm text-purple-600">Expected Cash</div>
                <div className="text-xl font-bold text-purple-800">
                  {formatCurrency(expectedCash)}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Breakdown */}
          {summary?.paymentBreakdown && summary.paymentBreakdown.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Payment Breakdown</h3>
              <div className="space-y-2">
                {summary.paymentBreakdown.map((payment) => (
                  <div
                    key={payment.paymentMethod}
                    className="flex justify-between items-center py-2 border-b last:border-0"
                  >
                    <div>
                      <span className="font-medium">
                        {payment.paymentMethod}
                      </span>
                      <span className="text-sm text-gray-500 ml-2">
                        ({payment._count} transactions)
                      </span>
                    </div>
                    <span className="font-semibold">
                      {formatCurrency(payment._sum.totalAmount || 0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invoice Count */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Total Invoices</span>
              <span className="text-xl font-bold">
                {summary?.invoiceCount || 0}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={() => navigate("/billing-sessions")}
              className="px-6 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => setStep("cash-count")}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Proceed to Close Session
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Closing Cash Count</h3>
          <p className="text-sm text-gray-500 mb-6">
            Count the cash in the drawer and enter the quantities below.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ₹2000 Notes
              </label>
              <input
                type="number"
                name="notes2000"
                value={cashCount.notes2000}
                onChange={handleCashCountChange}
                min="0"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ₹500 Notes
              </label>
              <input
                type="number"
                name="notes500"
                value={cashCount.notes500}
                onChange={handleCashCountChange}
                min="0"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ₹200 Notes
              </label>
              <input
                type="number"
                name="notes200"
                value={cashCount.notes200}
                onChange={handleCashCountChange}
                min="0"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ₹100 Notes
              </label>
              <input
                type="number"
                name="notes100"
                value={cashCount.notes100}
                onChange={handleCashCountChange}
                min="0"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ₹50 Notes
              </label>
              <input
                type="number"
                name="notes50"
                value={cashCount.notes50}
                onChange={handleCashCountChange}
                min="0"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ₹20 Notes
              </label>
              <input
                type="number"
                name="notes20"
                value={cashCount.notes20}
                onChange={handleCashCountChange}
                min="0"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ₹10 Notes
              </label>
              <input
                type="number"
                name="notes10"
                value={cashCount.notes10}
                onChange={handleCashCountChange}
                min="0"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coins (₹)
              </label>
              <input
                type="number"
                name="coins"
                value={cashCount.coins}
                onChange={handleCashCountChange}
                min="0"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Cash Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-purple-600">Expected Cash</div>
              <div className="text-xl font-bold text-purple-800">
                {formatCurrency(expectedCash)}
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600">Actual Cash Count</div>
              <div className="text-xl font-bold text-blue-800">
                {formatCurrency(calculateTotal())}
              </div>
            </div>
            <div
              className={`rounded-lg p-4 ${
                calculateTotal() - expectedCash === 0
                  ? "bg-green-50"
                  : calculateTotal() - expectedCash > 0
                    ? "bg-yellow-50"
                    : "bg-red-50"
              }`}
            >
              <div
                className={`text-sm ${
                  calculateTotal() - expectedCash === 0
                    ? "text-green-600"
                    : calculateTotal() - expectedCash > 0
                      ? "text-yellow-600"
                      : "text-red-600"
                }`}
              >
                Difference
              </div>
              <div
                className={`text-xl font-bold ${
                  calculateTotal() - expectedCash === 0
                    ? "text-green-800"
                    : calculateTotal() - expectedCash > 0
                      ? "text-yellow-800"
                      : "text-red-800"
                }`}
              >
                {calculateTotal() - expectedCash >= 0 ? "+" : ""}
                {formatCurrency(calculateTotal() - expectedCash)}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Closing Notes (Optional)
            </label>
            <textarea
              name="notes"
              value={cashCount.notes}
              onChange={handleCashCountChange}
              rows={2}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any notes about the closing..."
            />
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              onClick={() => setStep("summary")}
              className="px-6 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              onClick={handleCloseSession}
              disabled={loading}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? "Closing..." : "Close Session"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CloseBillingSessionPage;
